import React, { useEffect, useMemo, useState } from 'react';
import { useFarm } from '../../contexts/FarmContext';
import { activitiesAPI, aiAPI, farmAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  PlusIcon,
  CheckCircleIcon,
  TrashIcon,
  FunnelIcon,
  ChatBubbleOvalLeftEllipsisIcon
} from '@heroicons/react/24/outline';

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'badge-primary',
    overdue: 'badge-danger',
    completed: 'badge-success',
    cancelled: 'badge-secondary'
  };
  return <span className={`badge capitalize ${map[status] || 'badge-secondary'}`}>{status}</span>;
};

const checklistOf = (activity) => (
  Array.isArray(activity?.metadata?.checklist) ? activity.metadata.checklist : []
);

const isActivityDue = (activity) => {
  if (!activity) return false;
  if (activity.status === 'overdue') return true;
  const when = new Date(activity.scheduledDate).getTime();
  return Number.isFinite(when) && when <= Date.now();
};

const checklistProgress = (activity) => {
  const checklist = checklistOf(activity);
  const total = checklist.length;
  const doneCount = checklist.filter((step) => step.done).length;
  const skipped = checklist.filter((step) => !step.done);
  const percent = total > 0 ? Math.round((doneCount / total) * 100) : null;
  return { checklist, total, doneCount, skipped, percent };
};

const Activities = () => {
  const { currentFarm } = useFarm();
  const canEdit = ['owner','manager','editor'].includes(currentFarm?.currentUserRole);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [cohortId, setCohortId] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const load = async () => {
    if (!currentFarm) return;
    try {
      setLoading(true);
      // Load cohorts for filter/select
      try {
        const lc = await farmAPI.listCohorts(currentFarm.id);
        setCohorts(lc.data?.cohorts || []);
      } catch {}
      const res = await activitiesAPI.getActivities(currentFarm.id, { status, type, cohortId: cohortId || undefined, limit: pageSize, offset: (page - 1) * pageSize });
      setItems(res.data.activities || []);
      setTotal(Number(res.data.total || 0));
    } catch (e) {
      console.error('Load activities error', e);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => { load(); }, [currentFarm, status, type, cohortId, page, pageSize]);
useEffect(() => { setPage(1); }, [status, type, cohortId]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!currentFarm) return;
    const form = new FormData(e.currentTarget);
      const payload = {
      type: form.get('type'),
      title: form.get('title'),
      description: form.get('description') || undefined,
      scheduledDate: form.get('scheduledDate'),
        priority: form.get('priority') || 'medium',
        cohortId: form.get('cohortId') || undefined
    };
    try {
      setCreating(true);
      await activitiesAPI.createActivity(currentFarm.id, payload);
      if (e.currentTarget) { try { e.currentTarget.reset(); } catch(_) {} }
      setShowCreate(false);
      load();
    } catch (err) {
      console.error('Create activity error', err);
      alert(err.response?.data?.details || err.response?.data?.message || 'Failed to create activity');
    } finally { setCreating(false); }
  };

  const complete = async (activity) => {
    if (!isActivityDue(activity)) return;
    const checklist = checklistOf(activity);
    const remaining = checklist.filter((step) => !step.done).length;
    if (remaining > 0) {
      const ok = window.confirm(`${remaining} checklist step${remaining === 1 ? '' : 's'} still open. Mark this activity complete anyway?`);
      if (!ok) return;
    }
    try {
      await activitiesAPI.completeActivity(activity.id, 'Marked done from UI');
      load();
    } catch (e) {
      console.error('Complete error', e);
    }
  };

  const toggleStep = async (activity, item) => {
    if (!canEdit || activity.status === 'completed' || activity.status === 'cancelled' || !isActivityDue(activity)) return;
    const nextDone = !item.done;
    setItems((prev) => prev.map((row) => {
      if (row.id !== activity.id) return row;
      const checklist = (row.metadata?.checklist || []).map((step) => (
        step.id === item.id ? { ...step, done: nextDone } : step
      ));
      return { ...row, metadata: { ...row.metadata, checklist } };
    }));
    try {
      const res = await activitiesAPI.toggleChecklistItem(activity.id, item.id, nextDone);
      if (res.data?.activity) {
        setItems((prev) => prev.map((row) => row.id === activity.id ? res.data.activity : row));
      }
    } catch (e) {
      console.error('Checklist toggle error', e);
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await activitiesAPI.deleteActivity(id);
      load();
    } catch (e) {
      console.error('Delete error', e);
    }
  };

  const openAdvisor = (activity) => {
    setSelected(activity);
    setAiMessages([
      { role: 'system', content: 'You are an expert poultry farm assistant. Provide concise, actionable advice.' },
      { role: 'assistant', content: `Selected activity: ${activity.title} (${activity.type}), priority ${activity.priority}, scheduled for ${new Date(activity.scheduledDate).toLocaleString()}.` }
    ]);
    setAiOpen(true);
  };

  const sendAi = async () => {
    if (!aiInput.trim()) return;
    const msg = aiInput.trim();
    setAiMessages((m) => [...m, { role: 'user', content: msg }]);
    setAiInput('');
    try {
      setAiLoading(true);
      const selectedActivity = items.find((row) => row.id === selected?.id) || selected;
      const checklist = Array.isArray(selectedActivity?.metadata?.checklist) ? selectedActivity.metadata.checklist : [];
      const context = {
        activity: {
          id: selectedActivity?.id,
          type: selectedActivity?.type,
          title: selectedActivity?.title,
          status: selectedActivity?.status,
          priority: selectedActivity?.priority,
          scheduledDate: selectedActivity?.scheduledDate,
          description: selectedActivity?.description,
          checklist: checklist.length ? {
            total: checklist.length,
            doneCount: checklist.filter((step) => step.done).length,
            done: checklist.filter((step) => step.done).map((step) => step.label),
            skipped: checklist.filter((step) => !step.done).map((step) => step.label)
          } : undefined
        },
        farm: {
          id: currentFarm?.id,
          name: currentFarm?.name,
          chickenType: currentFarm?.setupData?.chickenType,
          chickenCount: currentFarm?.setupData?.chickenCount,
          ageDays: currentFarm?.setupData?.chickAge,
        }
      };
      const res = await aiAPI.chat({ message: msg, farmId: currentFarm?.id, context });
      setAiMessages((m) => [...m, { role: 'assistant', content: res.data?.response || 'No response' }]);
    } catch (e) {
      const fallback = e.response?.data?.response || e.response?.data?.recommendations || e.response?.data?.message || 'AI Assistant is unavailable.';
      setAiMessages((m) => [...m, { role: 'assistant', content: String(fallback) }]);
    } finally { setAiLoading(false); }
  };

  const types = ['vaccination','feeding','health_check','cleaning','harvest','other'];

  const pendingCount = items.filter(a => a.status === 'pending').length;
  const overdueCount = items.filter(a => a.status === 'overdue').length;
  const completedCount = items.filter(a => a.status === 'completed').length;

  return (
    <div className="relative">
      <div className="space-y-6 lg:pr-96">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Activities</h1>
                <p className="text-green-100">Tick each step as you work, then mark the activity complete</p>
              </div>
            </div>
            <button 
              className={`inline-flex items-center px-5 py-2.5 bg-white text-green-700 rounded-xl hover:bg-green-50 transition-colors font-medium ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`} 
              onClick={() => { if (!canEdit) return; setShowCreate(true); }} 
              disabled={!canEdit}
            >
              <PlusIcon className="h-5 w-5 mr-2" /> New Activity
            </button>
          </div>
          
          {/* Stats */}
          <div className="relative mt-6 grid grid-cols-3 gap-3">
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{pendingCount}</p>
              <p className="text-green-200 text-xs">Pending</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{overdueCount}</p>
              <p className="text-green-200 text-xs">Overdue</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{completedCount}</p>
              <p className="text-green-200 text-xs">Completed</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" value={status} onChange={(e)=>setStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <select className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all w-56" value={type} onChange={(e)=>setType(e.target.value)}>
              <option value="">All Types</option>
              {types.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
            </select>
            <select className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all w-64" value={cohortId} onChange={(e)=>setCohortId(e.target.value)}>
              <option value="">All Flocks</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} • {c.type}</option>)}
            </select>
          </div>
        </div>

        {/* List */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40"><LoadingSpinner /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No activities found.</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {items.map((a) => {
                const { checklist, total, doneCount, skipped, percent } = checklistProgress(a);
                const due = isActivityDue(a);
                const completed = a.status === 'completed';
                const cancelled = a.status === 'cancelled';
                const showLiveChecklist = !completed && !cancelled && due && total > 0;
                const showCompletedGaps = completed && total > 0 && percent < 100;
                return (
                <li key={a.id} className="p-4 hover:bg-gray-50 transition">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-gray-900">{a.title} {a.cohort ? <span className="text-gray-500">• {a.cohort.name}</span> : null}</h3>
                        <StatusBadge status={a.status} />
                        {completed && percent !== null && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            percent === 100 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {percent}% of steps done
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5 capitalize">{a.type.replace('_',' ')} • Priority {a.priority}</p>
                      <p className="text-xs text-gray-500">Scheduled: {new Date(a.scheduledDate).toLocaleString()}</p>
                      {a.description && <p className="text-sm text-gray-700 mt-1">{a.description}</p>}
                      {a.metadata?.category === 'hatching' && (
                        <p className="text-xs text-gray-500 mt-1">
                          {a.metadata?.insight ? a.metadata.insight : `Incubation day ${a.metadata?.day ?? ''}: Critical step for hatch success.`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap shrink-0">
                      <button className="btn-outline" onClick={() => openAdvisor(a)}>
                        <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4 mr-1"/> Ask AI
                      </button>
                      {canEdit && !completed && due && (
                        <button className="btn-success" onClick={()=>complete(a)}>
                          <CheckCircleIcon className="h-4 w-4 mr-1"/> Complete
                        </button>
                      )}
                      {canEdit ? (
                        <button className="btn-outline" onClick={()=>remove(a.id)}>
                          <TrashIcon className="h-4 w-4 mr-1"/> Delete
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {showLiveChecklist && (
                    <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Do this today</p>
                        <p className="text-xs text-gray-600">{doneCount}/{total} steps • {percent}%</p>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-200 mb-3 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-green-500 transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <ul className="space-y-2">
                        {checklist.map((step) => (
                          <li key={step.id}>
                            <label className={`flex items-start gap-2 text-sm ${!canEdit ? 'text-gray-500' : 'text-gray-800'} ${step.done ? 'line-through text-gray-500' : ''}`}>
                              <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                checked={Boolean(step.done)}
                                disabled={!canEdit}
                                onChange={() => toggleStep(a, step)}
                              />
                              <span>{step.label}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {showCompletedGaps && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Not done ({skipped.length} of {total})</p>
                      <ul className="mt-2 space-y-1">
                        {skipped.map((step) => (
                          <li key={step.id} className="text-sm text-amber-900">• {step.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-gray-600">
            Page {page} of {Math.max(1, Math.ceil(total / pageSize))} • {total} items
          </div>
          <div className="flex items-center gap-2">
            <select className="input py-1 px-2 text-sm" value={pageSize} onChange={(e)=>{ setPage(1); setPageSize(Number(e.target.value)); }}>
              {[10,20,50,100].map(s => <option key={s} value={s}>{s}/page</option>)}
            </select>
            <button className="btn-outline text-sm" onClick={()=> setPage(p => Math.max(1, p-1))} disabled={page<=1}>Prev</button>
            <button className="btn-outline text-sm" onClick={()=> setPage(p => (p*pageSize >= total ? p : p+1))} disabled={page*pageSize>=total}>Next</button>
          </div>
        </div>
      </div>

      {/* AI Advisor Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full lg:w-96 bg-white border-l border-gray-200 shadow-xl transform transition-transform duration-300 ${aiOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">AI Advisor</h3>
            <p className="text-xs text-gray-500">Context-aware guidance for your activity.</p>
          </div>
          <button className="btn-outline" onClick={()=>setAiOpen(false)}>Close</button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-8rem)]">
          {selected && (
            <div className="text-xs bg-gray-50 border border-gray-200 rounded p-3">
              <div className="font-medium text-gray-700">Selected:</div>
              <div className="text-gray-700">{selected.title} • {selected.type} • priority {selected.priority}</div>
            </div>
          )}
          {aiMessages.map((m, idx) => (
            <div key={idx} className={`${m.role === 'user' ? 'text-right' : 'text-left'}`}>
              <div className={`inline-block px-3 py-2 rounded-lg text-sm ${m.role === 'user' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {aiLoading && (
            <div className="text-gray-500 text-sm">Thinking...</div>
          )}
        </div>
        <div className="p-3 border-t flex items-center gap-2">
          <input 
            value={aiInput}
            onChange={(e)=>setAiInput(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); sendAi(); } }}
            placeholder="Ask about this activity (dosage, timing, risks, etc.)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
          <button className="btn-primary" onClick={sendAi} disabled={aiLoading}>Send</button>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold">New Activity</h3>
              <button className="inline-flex items-center px-3 py-1 rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow" onClick={()=>setShowCreate(false)}>Close</button>
            </div>
            <form onSubmit={onCreate} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="label">Type *</label>
                <select name="type" required className="input">
                  {types.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Flock</label>
                <select name="cohortId" className="input">
                  <option value="">No specific flock</option>
                  {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} • {c.type}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Title *</label>
                <input name="title" required className="input" placeholder="e.g., Newcastle vaccination"/>
              </div>
              <div>
                <label className="label">Scheduled Date *</label>
                <input type="datetime-local" name="scheduledDate" required className="input"/>
              </div>
              <div>
                <label className="label">Priority</label>
                <select name="priority" className="input">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="label">Description</label>
                <textarea name="description" rows={3} className="input" placeholder="Optional notes..."/>
              </div>
              <div className="pt-2 flex items-center justify-end gap-2">
                <button type="button" className="btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      {canEdit && (
        <button
          className="md:hidden fixed bottom-6 right-6 rounded-full bg-primary-600 text-white p-4 shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Add Activity"
          onClick={() => setShowCreate(true)}
        >
          <PlusIcon className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default Activities;
